import React, { useEffect, useState } from 'react';
import PaidActionConfirm from '../Common/PaidActionConfirm';
import { betaAiPaused } from '../../utils/betaSpendPause';
import { Link } from 'react-router-dom';
import { useTouchDevice } from '../../hooks/useTouchDevice';
import useCitations from '../../hooks/useCitations';
import { InlineSessionSuggestion } from '../Workshop/InlineSessionSuggestion';
import MarkdownMessage from './MarkdownMessage';
import FoundationOfframpCard from './FoundationOfframpCard';
import SeriesFocusOfferCard, {
  isSeriesFocusOfferDeclined,
  markSeriesFocusOfferDeclined,
  shouldShowSeriesFocusOffer,
  subscribeSeriesFocusOfferDecline,
} from './SeriesFocusOfferCard';
import SeriesBookCardOffer, { shouldShowSeriesBookCardOffer } from './SeriesBookCardOffer';
import PlotReadyOfferCard, {
  isPlotReadyOfferDeclined,
  markPlotReadyOfferDeclined,
  shouldShowPlotReadyOffer,
  subscribePlotReadyOfferDecline,
} from './PlotReadyOfferCard';
import BeatWorkshopOfferCard, {
  isBeatWorkshopOfferDeclined,
  markBeatWorkshopOfferDeclined,
  shouldShowBeatWorkshopOffer,
  subscribeBeatWorkshopOfferDecline,
} from './BeatWorkshopOfferCard';
import {
  isSeriesDiscoveryChat,
  seriesDiscoveryReplyText,
} from '../../utils/seriesDiscoveryReply';
import AddToBibleModal from './AddToBibleModal';
import ChatIdeaOffers from './ChatIdeaOffers';
import SaveToCharacterModal from './SaveToCharacterModal';
import SaveToSeriesNotesModal from './SaveToSeriesNotesModal';
import ErrorMessage from '../Common/ErrorMessage';
import { shouldShowFor } from './postConversionNotification';
import { NUDGE_MARKER_MESSAGE } from '../../api/chat';
import NudgeBatch from './NudgeBatch';
import { useSourceViewer } from '../DocumentImport/SourceViewerContext';
import {
  ADDED_TO_BOOK_NOTES,
  ADD_TO_BOOK_NOTES,
  SAVE_TO_BOOK_NOTES,
  SAVE_TO_SERIES_NOTES,
  SAVED_TO_SERIES_NOTES,
} from '../../utils/writerCopy';
import {
  STREAMING_REPLY_WAIT,
  isEmptyStreamingReply,
  streamingReplyDisplay,
} from '../../utils/streamingReplyDisplay';
import { REPLY_STOPPED_EARLY_MESSAGE } from '../../utils/betaAllowance';
import './Chat.css';

const ChatMessage = ({
  message,
  onBookmark,
  citationMap,
  isHighlighted = false,
  isBookmarked = false,
  // Session suggestion props
  sessionId,
  storyId,
  projectId,
  canSaveToSeriesNotes = false,
  onNavigateToSession,
  pendingFoundation,
  onSuggestionDismissed,
  onSeriesFocusOfferAccept,
  onSeriesBookCardAccept,
  onPlotReadyOfferAccept,
  onPlotReadyOfferDecline,
  onBeatWorkshopAccept,
  isLastMessage = false,
  onRegenerate,
  chatModelId = null,
  messageCount = null,
  discovery = false,
  estimateInputTokens = null,
  onNudgeVote,
  ideaOffers = [],
  onIdeaOfferResolved,
}) => {
  const isTouch = useTouchDevice();
  const [seriesFocusOfferDismissed, setSeriesFocusOfferDismissed] = useState(
    () => isSeriesFocusOfferDeclined(sessionId)
  );
  const [plotReadyOfferDismissed, setPlotReadyOfferDismissed] = useState(
    () => isPlotReadyOfferDeclined(sessionId, message.id)
  );
  const [beatWorkshopOfferDismissed, setBeatWorkshopOfferDismissed] = useState(
    () => isBeatWorkshopOfferDeclined(sessionId, message.id)
  );
  const [showActions, setShowActions] = useState(false);
  const [copied, setCopied] = useState(false);
  const [addedToBible, setAddedToBible] = useState(false);
  const [showAddToBible, setShowAddToBible] = useState(false);
  const [savedToCharacter, setSavedToCharacter] = useState(false);
  const [showSaveToCharacter, setShowSaveToCharacter] = useState(false);
  const [showSaveToSeries, setShowSaveToSeries] = useState(false);
  const [savedToSeries, setSavedToSeries] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState(null);
  const { scrollToMessage, renderWithCitations } = useCitations();
  const { openImportChunkCitation } = useSourceViewer();

  useEffect(() => {
    setSeriesFocusOfferDismissed(isSeriesFocusOfferDeclined(sessionId));
    return subscribeSeriesFocusOfferDecline((id) => {
      if (id === sessionId) setSeriesFocusOfferDismissed(true);
    });
  }, [sessionId]);

  const dismissSeriesFocusOffer = () => {
    markSeriesFocusOfferDeclined(sessionId);
    setSeriesFocusOfferDismissed(true);
  };

  useEffect(() => {
    setPlotReadyOfferDismissed(isPlotReadyOfferDeclined(sessionId, message.id));
    return subscribePlotReadyOfferDecline((id, declinedMessageId) => {
      if (id === sessionId && declinedMessageId === message.id) setPlotReadyOfferDismissed(true);
    });
  }, [sessionId, message.id]);

  const dismissPlotReadyOffer = () => {
    markPlotReadyOfferDeclined(sessionId, message.id);
    setPlotReadyOfferDismissed(true);
    onPlotReadyOfferDecline?.();
  };

  useEffect(() => {
    setBeatWorkshopOfferDismissed(isBeatWorkshopOfferDeclined(sessionId, message.id));
    return subscribeBeatWorkshopOfferDecline((id, declinedMessageId) => {
      if (id === sessionId && declinedMessageId === message.id) setBeatWorkshopOfferDismissed(true);
    });
  }, [sessionId, message.id]);

  const dismissBeatWorkshopOffer = () => {
    markBeatWorkshopOfferDeclined(sessionId, message.id);
    setBeatWorkshopOfferDismissed(true);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const [pendingRegenerate, setPendingRegenerate] = useState(false);
  const [allowancePause, setAllowancePause] = useState(false);

  const handleRegenerate = () => {
    if (!onRegenerate || isRegenerating || pendingRegenerate) return;
    if (betaAiPaused()) {
      setAllowancePause(true);
      return;
    }
    setPendingRegenerate(true);
  };

  const cancelRegenerate = () => setPendingRegenerate(false);

  const confirmRegenerate = async () => {
    if (!onRegenerate || isRegenerating) {
      setPendingRegenerate(false);
      return;
    }
    setPendingRegenerate(false);
    setIsRegenerating(true);
    setRegenerateError(null);
    try {
      await onRegenerate(message.id);
    } catch (err) {
      setRegenerateError(err.message || 'Failed to regenerate response. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const isUser = message.speaker === 'user';
  const formattedTime = new Date(message.created_at).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  });

  // Nudge marker (nudge-button-spec.md FQ1): a small, visible, persisted
  // divider — not a normal chat bubble — so the writer can read back why
  // the AI suddenly offered directions. Prefers metadata.kind; content
  // match remains for rows written before #605.
  const nudgeMeta = message.metadata || {};
  const isNudgeMarker = isUser && (
    nudgeMeta.kind === 'nudge_marker' || message.content === NUDGE_MARKER_MESSAGE
  );
  const isNudgeBatch = !isUser && nudgeMeta.kind === 'nudge_batch' && Array.isArray(nudgeMeta.items) && nudgeMeta.items.length > 0;
  if (isNudgeMarker) {
    return (
      <div id={`message-${message.id}`} className="chat-message chat-message--nudge-marker">
        <span className="chat-message--nudge-marker__text">Nudge requested</span>
        <span className="chat-message__time">{formattedTime}</span>
      </div>
    );
  }

  const handleCitationClick = async (messageId) => {
    const citationData = citationMap?.[messageId];
    const opened = await openImportChunkCitation(messageId, citationData);
    if (opened) return;
    scrollToMessage(`message-${messageId}`);
  };

  const renderContent = () => {
    const parts = renderWithCitations(message.content, citationMap);

    return parts.map((part) => {
      if (part.type === 'text') {
        // Preserve newlines in text
        return part.content.split('\n').map((line, i, arr) => (
          <React.Fragment key={`${part.key}-line-${i}`}>
            {line}
            {i < arr.length - 1 && <br />}
          </React.Fragment>
        ));
      } else if (part.type === 'citation') {
        return (
          <span
            key={part.key}
            className="chat-message__citation"
            onClick={() => handleCitationClick(part.messageId)}
            title={part.citationData ? `Jump to message: ${part.citationData.preview}` : 'Referenced message'}
          >
            [{part.messageId}]
          </span>
        );
      }
      return null;
    });
  };

  // #978 — empty in-flight bubble gets an honest wait line. Do not persist
  // STREAMING_REPLY_WAIT on the message; the first token replaces it.
  const waitingForReply = !isUser && isEmptyStreamingReply(message);
  const replyStopped = !isUser && !message.streaming && (
    message.interrupted || message.metadata?.reply_interrupted
  );
  const assistantBody = (
    !message.streaming && isSeriesDiscoveryChat({ projectId, storyId })
      ? seriesDiscoveryReplyText(message.content)
      : message.content
  );
  const assistantDisplay = waitingForReply
    ? STREAMING_REPLY_WAIT
    : streamingReplyDisplay(assistantBody, { streaming: message.streaming });

  const messageClasses = [
    'chat-message',
    isUser ? 'chat-message--user' : 'chat-message--assistant',
    isHighlighted ? 'chat-message--highlighted' : '',
    message.streaming ? 'chat-message--streaming' : '',
    waitingForReply ? 'chat-message--streaming-wait' : '',
    message.interrupted ? 'chat-message--interrupted' : ''
  ].filter(Boolean).join(' ');

  // #756 — Focus must not share a stack with a book-card offer.
  const bookCardShowing = shouldShowSeriesBookCardOffer(
    message.metadata?.series_book_card_offer,
    sessionId,
  );
  const focusShowing = shouldShowSeriesFocusOffer(
    message.metadata?.series_focus_offer,
    sessionId,
    { bookCardShowing },
  );
  const plotReadyShowing = shouldShowPlotReadyOffer(
    message.metadata?.plot_ready_offer,
    sessionId,
    message.id,
  );
  const beatWorkshopShowing = shouldShowBeatWorkshopOffer(
    message.metadata?.beat_workshop_offer,
    sessionId,
    message.id,
  );
  return (
    <div
      id={`message-${message.id}`}
      className={messageClasses}
      aria-busy={message.streaming ? true : undefined}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {allowancePause ? (
        <PaidActionConfirm paused onCancel={() => setAllowancePause(false)} />
      ) : null}
      {pendingRegenerate ? (
        <PaidActionConfirm
          actionId="regenerate"
          modelId={chatModelId}
          inputTokens={estimateInputTokens}
          messageCount={messageCount}
          discovery={discovery}
          onCancel={cancelRegenerate}
          onConfirm={confirmRegenerate}
        />
      ) : null}
      <div className="chat-message__header">
        <span className="chat-message__speaker">
          {isUser ? 'You' : 'Brainstormy'}
        </span>
        <span className="chat-message__time">{formattedTime}</span>
      </div>
      <div className={`chat-message__content${isUser || isNudgeBatch ? '' : ' chat-message__content--markdown'}`}>
        {isUser ? (
          renderContent()
        ) : isNudgeBatch && !message.streaming ? (
          <NudgeBatch
            items={nudgeMeta.items}
            hatch={nudgeMeta.hatch}
            onVote={onNudgeVote ? (itemId, vote) => onNudgeVote(message.id, itemId, vote) : null}
          />
        ) : (
          <MarkdownMessage
            content={assistantDisplay}
            citationMap={citationMap}
            onCitationClick={handleCitationClick}
          />
        )}
        {!isUser && typeof message.metadata?.sequence_purpose_more === 'string' && message.metadata.sequence_purpose_more ? (
          <details className="sequence-purpose">
            <summary className="sequence-purpose__summary">More about this sequence</summary>
            <p className="sequence-purpose__more">{message.metadata.sequence_purpose_more}</p>
          </details>
        ) : null}
      </div>
      {replyStopped ? (
        <p className="chat-message__stopped" role="status">{REPLY_STOPPED_EARLY_MESSAGE}</p>
      ) : null}
      {/* Render exploration suggestions for assistant messages (Logline Workshop Enhancement) */}
      {!isUser && message.suggestions?.length > 0 && sessionId && storyId && (
        <div className="chat-message__suggestions">
          {message.suggestions.map((suggestion, index) => (
            <InlineSessionSuggestion
              key={index}
              suggestion={suggestion}
              parentSessionId={sessionId}
              storyId={storyId}
              onSessionCreated={onNavigateToSession}
            />
          ))}
        </div>
      )}
      {/* Foundation offramp doors — optional continuation choices that open when
          the writer confirms their foundation (single-book). */}
      {!isUser && pendingFoundation?.confirmed && (
        <FoundationOfframpCard
          foundation={pendingFoundation}
          onNavigateToSession={onNavigateToSession}
          onDismiss={onSuggestionDismissed}
        />
      )}
      {!isUser && projectId && !seriesFocusOfferDismissed && focusShowing && (
        <SeriesFocusOfferCard
          sessionId={sessionId}
          onAccept={onSeriesFocusOfferAccept}
          onDecline={dismissSeriesFocusOffer}
        />
      )}
      {!isUser && projectId && bookCardShowing && (
        <SeriesBookCardOffer
          sessionId={sessionId}
          offer={message.metadata.series_book_card_offer}
          onAccept={onSeriesBookCardAccept}
        />
      )}
      {!isUser && storyId && !plotReadyOfferDismissed && plotReadyShowing && (
        <PlotReadyOfferCard
          sessionId={sessionId}
          messageId={message.id}
          onAccept={() => onPlotReadyOfferAccept?.(message.metadata?.plot_ready_offer)}
          onDecline={dismissPlotReadyOffer}
        />
      )}
      {!isUser && storyId && !beatWorkshopOfferDismissed && beatWorkshopShowing && (
        <BeatWorkshopOfferCard
          sessionId={sessionId}
          messageId={message.id}
          offer={message.metadata?.beat_workshop_offer}
          draft={message.content}
          onAccept={onBeatWorkshopAccept}
          onDecline={dismissBeatWorkshopOffer}
        />
      )}
      {/* Post-conversion inline notification — appears once on the next AI message */}
      {!isUser && projectId && (() => {
        const bookDiscoveryId = shouldShowFor(projectId, message.id, message.created_at);
        if (!bookDiscoveryId) return null;
        return (
          <div className="post-conversion-note" role="note">
            <span className="post-conversion-note__icon">📚</span>
            <span className="post-conversion-note__text">
              Book 1 has been added to your series. A Discovery session is ready
              there whenever you want to start developing the first novel.{' '}
              <Link to={`/chat/${bookDiscoveryId}`} className="post-conversion-note__link">
                Go to Book 1 Discovery →
              </Link>
            </span>
          </div>
        );
      })()}
      {!message.streaming && storyId && ideaOffers.length > 0 && (
        <ChatIdeaOffers
          offers={ideaOffers}
          storyId={storyId}
          onResolved={onIdeaOfferResolved}
        />
      )}
      {/* Step 0 (#2066). The reply is stamped step0_reply. Show the existing
          Book Notes buttons without a hover — the icon row was easy to miss. */}
      {!isUser && !message.streaming && storyId && message.metadata?.step0_reply && (message.content || '').trim() && (
        <div className="chat-message__step0-saves" role="group" aria-label="Save this reply">
          <button
            type="button"
            className="chat-message__save-series"
            onClick={() => setShowAddToBible(true)}
          >
            {addedToBible ? `✓ ${ADDED_TO_BOOK_NOTES}` : ADD_TO_BOOK_NOTES}
          </button>
          <button
            type="button"
            className="chat-message__save-series"
            onClick={() => setShowSaveToCharacter(true)}
          >
            {savedToCharacter ? '✓ Saved to Book Notes' : SAVE_TO_BOOK_NOTES}
          </button>
        </div>
      )}
      {/* Save to series notes (#1417) — series-level chats only, always visible (run 20: a
          hover-only icon was never found). Word for word into one new note. */}
      {!message.streaming && canSaveToSeriesNotes && projectId && (message.content || '').trim() && (
        <button
          type="button"
          className="chat-message__save-series"
          onClick={() => setShowSaveToSeries(true)}
        >
          {savedToSeries ? `✓ ${SAVED_TO_SERIES_NOTES}` : SAVE_TO_SERIES_NOTES}
        </button>
      )}
      {!message.streaming && (showActions || isBookmarked || copied || isTouch || isRegenerating || regenerateError) && (
        <div className="chat-message__actions">
          <button
            className={`chat-message__action-button ${copied ? 'chat-message__action-button--success' : ''}`}
            onClick={handleCopy}
            title={copied ? "Copied!" : "Copy to clipboard"}
          >
            <span className="chat-message__action-icon">{copied ? '✓' : '📋'}</span>
          </button>
          {/* Regenerate — assistant messages only, and only while this is the
              session's most recent message (regenerate-response-spec.md).
              Hides once a new message is sent because isLastMessage is
              computed live from the messages array, not tracked separately. */}
          {!isUser && isLastMessage && onRegenerate && !message.streaming && (
            <button
              type="button"
              className="chat-message__action-button"
              onClick={handleRegenerate}
              disabled={isRegenerating}
              title={isRegenerating ? 'Regenerating…' : 'Regenerate this response'}
              aria-label={isRegenerating ? 'Regenerating response' : 'Regenerate this response'}
            >
              <span className="chat-message__action-icon">{isRegenerating ? '⏳' : '🔄'}</span>
            </button>
          )}
          {onBookmark && (
            <button
              className={`chat-message__action-button ${isBookmarked ? 'chat-message__action-button--active' : ''}`}
              onClick={() => onBookmark(message.id)}
              title={isBookmarked ? "Remove bookmark" : "Bookmark this message"}
            >
              <span className="chat-message__action-icon">{isBookmarked ? '📍' : '📌'}</span>
            </button>
          )}
          {/* Add to Bible — both user and assistant messages, story sessions.
              A fact the writer stated in their own message is the purest
              user-authored canon, so the action is symmetric across speakers. */}
          {storyId && (
            <button
              className={`chat-message__action-button ${addedToBible ? 'chat-message__action-button--success' : ''}`}
              onClick={() => setShowAddToBible(true)}
              title={addedToBible ? ADDED_TO_BOOK_NOTES : ADD_TO_BOOK_NOTES}
            >
              <span className="chat-message__action-icon">{addedToBible ? '✓' : '🗂️'}</span>
            </button>
          )}
          {/* Save to Bible — attach this message verbatim to an EXISTING
              character as a note (distinct from "Add to Bible" above, which
              segments the message into new canon entries). Picker only lists
              characters that already exist; nothing is created from here. */}
          {storyId && (
            <button
              className={`chat-message__action-button ${savedToCharacter ? 'chat-message__action-button--success' : ''}`}
              onClick={() => setShowSaveToCharacter(true)}
              title={savedToCharacter ? 'Saved to character' : SAVE_TO_BOOK_NOTES}
            >
              <span className="chat-message__action-icon">{savedToCharacter ? '✓' : '📝'}</span>
            </button>
          )}
        </div>
      )}
      {regenerateError && (
        <ErrorMessage message={regenerateError} onRetry={handleRegenerate} />
      )}
      {showAddToBible && storyId && (
        <AddToBibleModal
          storyId={storyId}
          messageContent={message.content}
          messageId={message.id}
          onClose={() => setShowAddToBible(false)}
          onSaved={() => setAddedToBible(true)}
        />
      )}
      {showSaveToSeries && projectId && (
        <SaveToSeriesNotesModal
          projectId={projectId}
          messageContent={message.content}
          onClose={() => setShowSaveToSeries(false)}
          onSaved={() => setSavedToSeries(true)}
        />
      )}
      {showSaveToCharacter && storyId && (
        <SaveToCharacterModal
          storyId={storyId}
          messageContent={message.content}
          messageId={message.id}
          sessionId={sessionId}
          onClose={() => setShowSaveToCharacter(false)}
          onSaved={() => setSavedToCharacter(true)}
        />
      )}
    </div>
  );
};

export default ChatMessage;
